import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { ApiService } from './api.service';
import { ApiResponse, PaginatedResponse } from '../interfaces/api-response.interface';
import { Property, Role, User, UserPayload, UserProfilePayload } from '../interfaces/user.interface';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private api = inject(ApiService);

  private _currentUser$ = new BehaviorSubject<User | null>(null);
  currentUser$ = this._currentUser$.asObservable();
  private readonly USER_ID_KEY = 'ks_userid';
  private readonly USER_EMAIL_KEY = 'ks_email';
  private readonly USER_ROLE_KEY = 'ks_role';

  get currentUser(): User | null {
    return this._currentUser$.getValue();
  }

  saveUserToLocal(user: User): void {
    localStorage.setItem(this.USER_ID_KEY, String(user.userId));
    localStorage.setItem(this.USER_EMAIL_KEY, user.email ?? '');
    localStorage.setItem(this.USER_ROLE_KEY, user.roleName ?? '');
  }

  getUserFromLocal(): { userId: number; email: string; role: string } | null {
    const userId = localStorage.getItem(this.USER_ID_KEY);
    const email = localStorage.getItem(this.USER_EMAIL_KEY);
    const role = localStorage.getItem(this.USER_ROLE_KEY);

    if (!userId || !email) return null;

    return {
      userId: Number(userId),
      email: email,
      role: role || ''
    };
  }

  clearUserFromLocal(): void {
    localStorage.removeItem(this.USER_ID_KEY);
    localStorage.removeItem(this.USER_EMAIL_KEY);
    localStorage.removeItem(this.USER_ROLE_KEY);
  }

  loadUserDetails(): Observable<ApiResponse<User>> {
    return this.api.get<ApiResponse<User>>('/user/get-user-details').pipe(
      tap(res => {
        if (res.data) {
          this._currentUser$.next(res.data);
          this.saveUserToLocal(res.data);
        }
      })
    );
  }

  private roleChangedSubject = new BehaviorSubject<boolean>(false);
  roleChanged$ = this.roleChangedSubject.asObservable();

  notifyRoleChanged(updatedUserId: number) {
    if (this.currentUser?.userId === updatedUserId) {
      this.loadUserDetails().subscribe({
        next: () => {
          this.roleChangedSubject.next(true);
        },
        error: (err) => {
          console.error('Failed to refresh user details after role change', err);
        }
      });
    }
  }

  getUserList(
    pageNumber: number,
    pageSize: number,
    searchTerm = '',
    sortField = '',
    sortDirection = ''
  ): Observable<ApiResponse<PaginatedResponse<User>>> {
    const params: Record<string, string | number> = {
      pageNumber: pageNumber,
      pageSize: pageSize,
    };

    if (searchTerm) params['search'] = searchTerm;
    if (sortField) params['sortField'] = sortField;
    if (sortDirection) params['sortDirection'] = sortDirection;

    return this.api.post<ApiResponse<PaginatedResponse<User>>>('/user/list-all', params);
  }

  addUser(userData: Partial<UserPayload>): Observable<ApiResponse<User>> {
    return this.api.post<ApiResponse<User>>('/user/create', userData);
  }

  updateUser(userData: Partial<UserPayload>): Observable<ApiResponse<User>> {
    return this.api.post<ApiResponse<User>>('/user/edit', userData);
  }

  deleteUser(userData: Partial<UserPayload>): Observable<ApiResponse<User>> {
    return this.api.post<ApiResponse<User>>('/user/delete', userData);
  }

  getPropertyList(): Observable<ApiResponse<Property[]>> {
    return this.api.post<ApiResponse<Property[]>>(`/user/properties-list`, {});
  }

  getRoleList(): Observable<ApiResponse<Role[]>> {
    return this.api.post<ApiResponse<Role[]>>(`/user/roles-list`, {});
  }

  resendInvitation(email: string): Observable<ApiResponse<null>> {
    return this.api.post<ApiResponse<null>>('/user/resend-invitation', { email });
  }

  getUserDetails(): Observable<ApiResponse<User>> {
    return this.api.get<ApiResponse<User>>('/user/get-user-details');
  }
  
  sendOtpForProfile(verificationType: number): Observable<ApiResponse<null>> {
    return this.api.post<ApiResponse<null>>('/user/request-profile-otp', { verificationType });
  }

  updatePassword(currentPassword: string, newPassword: string): Observable<ApiResponse<null>> {
    return this.api.post<ApiResponse<null>>('/auth/changepassword', { currentPassword, newPassword });
  }

  updateProfile(userData: Partial<UserProfilePayload>): Observable<ApiResponse<User>> {
    return this.api.put<ApiResponse<User>>('/user/update-profile', userData);
  }

  updateCookieConsent() {
    return this.api.post(`/user/accept-cookie`, {  });
  }

  toggleFileUpload(): Observable<ApiResponse<{ isFileUploadEnabled: boolean }>> {
    return this.api.put<ApiResponse<{ isFileUploadEnabled: boolean }>>(
      '/user/toggle-file-upload', {}
    ).pipe(
      tap(res => {
        if (res.data && this._currentUser$.value) {
          const updated = {
            ...this._currentUser$.value,
            isFileUploadEnabled: res.data.isFileUploadEnabled
          };
          this._currentUser$.next(updated);
        }
      })
    );
  }

}